import { Card, CardContent, Box, Typography, LinearProgress } from '@mui/material'
import { TrendingUp, EmojiEvents } from '@mui/icons-material'
import { motion } from 'framer-motion'
import { calculateLevel } from '@/lib/achievements'

interface LevelProgressProps {
  points: number
  userName: string
}

export default function LevelProgress({ points, userName }: LevelProgressProps) {
  const { level, progress, nextLevelPoints } = calculateLevel(points)

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
              {userName}'s Level
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {level}
              </Typography>
              <TrendingUp sx={{ fontSize: 32 }} />
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 3,
              px: 2,
              py: 1,
              textAlign: 'center',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
              Total Points
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmojiEvents sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {points}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
              Progress to Level {level + 1}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
              {Math.round(progress)}%
            </Typography>
          </Box>

          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 12,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'white',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(255,255,255,0.3)',
                },
              }}
            />
            <motion.div
              animate={{
                opacity: [0.5, 1, 0.5],
              }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 12,
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              }}
            />
          </Box>

          <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.8 }}>
            {nextLevelPoints} points until next level
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: '1px solid rgba(255,255,255,0.2)',
            display: 'flex',
            justifyContent: 'space-around',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {level}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Current Level
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {level + 1}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Next Level
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {nextLevelPoints}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Points Needed
            </Typography>
          </Box>
        </Box>
      </CardContent>

      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          opacity: 0.1,
          transform: 'rotate(-15deg)',
        }}
      >
        <EmojiEvents sx={{ fontSize: 200 }} />
      </Box>
    </Card>
  )
}
